/**
 * PDP Arber framing: cart line-item properties + live framed preview canvas.
 * Works in the Dawn product form (Medium option) and in forms that only sell
 * framed acrylic (data-force-framed).
 */
(function () {
  var DEFAULT_FRAME_ID = '535000';
  var DEFAULT_LINER_ID = 'none';
  var LINER_IN = 3;
  var FACE_IN = 3.5;
  var imageCache = {};
  var ARBER_FINISH_RGB = {
    '535000': [36, 34, 32],
    '859003': [96, 94, 90],
    '535087': [68, 44, 32],
    '535009': [226, 222, 214],
    '535086': [98, 62, 38],
    '60645': [62, 51, 41],
    '60687': [72, 49, 38],
    '60610': [131, 70, 39],
    '99801087': [72, 49, 34],
    '99901087': [72, 49, 34],
    '99801069': [51, 45, 39],
    '99901069': [51, 45, 39],
    '99902087': [83, 52, 33],
    '99805218': [25, 24, 22],
    '99802083': [174, 101, 43],
    '99902069': [61, 48, 42],
    '99805219': [31, 27, 25],
    '99805217': [162, 73, 35],
  };
  var PROP_KEYS = [
    'Frame finish',
    'Linen liner',
    'Frame Roma SKU',
    'Liner Roma SKU',
    'Overall outer',
  ];

  function framedOuter(printW, printH, face, liner) {
    var pw = Number(printW);
    var ph = Number(printH);
    if (!isFinite(pw) || !isFinite(ph) || pw <= 0 || ph <= 0) return null;
    var f = isFinite(Number(face)) && Number(face) >= 0 ? Number(face) : FACE_IN;
    var l = isFinite(Number(liner)) && Number(liner) >= 0 ? Number(liner) : LINER_IN;
    return {
      printWidth: pw,
      printHeight: ph,
      linerInches: l,
      faceWidthInches: f,
      linerOuterWidth: pw + 2 * l,
      linerOuterHeight: ph + 2 * l,
      outerWidth: pw + 2 * (l + f),
      outerHeight: ph + 2 * (l + f),
    };
  }

  function parseSize(text) {
    var m = String(text || '').match(/(\d+)\s*[x×]\s*(\d+)/i);
    if (!m) return null;
    return { w: Number(m[1]), h: Number(m[2]) };
  }

  function isFramedText(text) {
    return String(text || '').toLowerCase().indexOf('framed') >= 0;
  }

  function pick(list, wantId, fallbackId) {
    var items = list || [];
    var id = wantId || fallbackId;
    for (var i = 0; i < items.length; i += 1) {
      if (items[i].id === id) return items[i];
    }
    return items[0] || null;
  }

  function roundIn(n) {
    return Math.round(n * 10) / 10;
  }

  function loadImage(url) {
    if (!url) return Promise.resolve(null);
    if (imageCache[url]) return imageCache[url];
    imageCache[url] = new Promise(function (resolve) {
      var img = new Image();
      img.decoding = 'async';
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        resolve(null);
      };
      img.src = url;
    });
    return imageCache[url];
  }

  function readyImage(img) {
    return img && img.complete && img.naturalWidth > 0;
  }

  function fillCover(ctx, img, x, y, w, h) {
    if (!readyImage(img) || w <= 0 || h <= 0) return;
    var iw = img.naturalWidth;
    var ih = img.naturalHeight;
    var scale = Math.max(w / iw, h / ih);
    var dw = iw * scale;
    var dh = ih * scale;
    ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  }

  function frameRgb(frame) {
    if (frame && ARBER_FINISH_RGB[frame.id]) return ARBER_FINISH_RGB[frame.id];
    if (frame && Array.isArray(frame.rgb) && frame.rgb.length === 3) return frame.rgb;
    return [40, 38, 36];
  }

  function drawMoulding(ctx, frame, atlas, width, height, face) {
    var api = typeof GWFrameReconstruct !== 'undefined' ? GWFrameReconstruct : null;
    if (api && api.drawReconstructedFrame(ctx, atlas, width, height, face)) return;
    if (api && api.fillFallbackFrame) {
      api.fillFallbackFrame(ctx, width, height, face, frameRgb(frame));
      return;
    }
    ctx.fillStyle =
      'rgb(' +
      frameRgb(frame)[0] +
      ',' +
      frameRgb(frame)[1] +
      ',' +
      frameRgb(frame)[2] +
      ')';
    ctx.beginPath();
    ctx.rect(0, 0, width, height);
    ctx.rect(face, face, Math.max(0, width - face * 2), Math.max(0, height - face * 2));
    ctx.fill('evenodd');
  }

  function clipPolygon(ctx, points) {
    ctx.beginPath();
    ctx.moveTo(points[0][0], points[0][1]);
    for (var i = 1; i < points.length; i += 1) {
      ctx.lineTo(points[i][0], points[i][1]);
    }
    ctx.closePath();
    ctx.clip();
  }

  /** Fill a liner band with one seamless stretch — never tile (tiles read as gridlines). */
  function fillLinerBand(ctx, img, x, y, w, h, fallback) {
    if (w <= 0 || h <= 0) return;
    if (readyImage(img)) {
      ctx.drawImage(img, x, y, w, h);
      return;
    }
    fillRectColor(ctx, x, y, w, h, fallback || '#f5f2eb');
  }

  /** Four trapezoids give the linen liner proper 45° miter joints. */
  function drawLinerRails(ctx, img, x, y, width, height, band, fallback) {
    if (band <= 0) return;

    function paint(length) {
      fillLinerBand(ctx, img, 0, 0, length, band, fallback);
    }

    ctx.save();
    clipPolygon(ctx, [
      [x, y],
      [x + width, y],
      [x + width - band, y + band],
      [x + band, y + band],
    ]);
    ctx.translate(x, y);
    paint(width);
    ctx.restore();

    ctx.save();
    clipPolygon(ctx, [
      [x + band, y + height - band],
      [x + width - band, y + height - band],
      [x + width, y + height],
      [x, y + height],
    ]);
    ctx.translate(x + width, y + height);
    ctx.rotate(Math.PI);
    paint(width);
    ctx.restore();

    ctx.save();
    clipPolygon(ctx, [
      [x, y],
      [x + band, y + band],
      [x + band, y + height - band],
      [x, y + height],
    ]);
    ctx.translate(x, y + height);
    ctx.rotate(-Math.PI / 2);
    paint(height);
    ctx.restore();

    ctx.save();
    clipPolygon(ctx, [
      [x + width, y],
      [x + width, y + height],
      [x + width - band, y + height - band],
      [x + width - band, y + band],
    ]);
    ctx.translate(x + width, y);
    ctx.rotate(Math.PI / 2);
    paint(height);
    ctx.restore();
  }

  function fillRectColor(ctx, x, y, w, h, color) {
    if (w <= 0 || h <= 0) return;
    ctx.fillStyle = color;
    ctx.fillRect(x, y, w, h);
  }

  // Slightly off-black so charcoal frames separate from the PDP #1a1a1a field.
  var PREVIEW_PAD_PX = 5;
  var PREVIEW_BACKDROP = '#2a2a2a';

  function drawPreview(canvas, geom, printImg, frame, frameTexture, linerImg, linerFill) {
    if (!canvas || !geom) return;
    var maxCss = Number(canvas.parentNode && canvas.parentNode.getAttribute('data-preview-max')) || 300;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var frameW = maxCss;
    var frameH = Math.max(1, Math.round(maxCss * (geom.outerHeight / geom.outerWidth)));
    var pad = PREVIEW_PAD_PX;
    var cssW = frameW + pad * 2;
    var cssH = frameH + pad * 2;
    var scale = frameW / geom.outerWidth;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    var ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);
    ctx.imageSmoothingEnabled = true;
    if (ctx.imageSmoothingQuality) ctx.imageSmoothingQuality = 'high';

    fillRectColor(ctx, 0, 0, cssW, cssH, PREVIEW_BACKDROP);

    ctx.save();
    ctx.translate(pad, pad);

    var px = scale;
    var face = geom.faceWidthInches * px;
    var liner = geom.linerInches * px;
    var printW = geom.printWidth * px;
    var printH = geom.printHeight * px;
    var linerX = face;
    var linerY = face;
    var linerW = geom.linerOuterWidth * px;
    var linerH = geom.linerOuterHeight * px;
    var printX = face + liner;
    var printY = face + liner;

    drawMoulding(ctx, frame, frameTexture, frameW, frameH, face);

    var linen = linerFill || '#f5f2eb';
    drawLinerRails(ctx, linerImg, linerX, linerY, linerW, linerH, liner, linen);

    // Cover the liner “well” with print so band corners don't show seams.
    ctx.save();
    ctx.beginPath();
    ctx.rect(printX, printY, printW, printH);
    ctx.clip();
    ctx.fillStyle = '#111';
    ctx.fillRect(printX, printY, printW, printH);
    fillCover(ctx, printImg, printX, printY, printW, printH);
    ctx.restore();
    ctx.restore();
  }

  function initPanel(root) {
    if (root.getAttribute('data-framing-init') === 'true') return;
    root.setAttribute('data-framing-init', 'true');

    var form = root.closest('form');
    if (!form) return;

    var catalogUrl = root.getAttribute('data-catalog-url') || '';
    var forced = root.getAttribute('data-force-framed') === 'true';
    var frameHost = root.querySelector('[data-frame-swatches]');
    var linerHost = root.querySelector('[data-liner-swatches]');
    var outerLabel = root.querySelector('[data-outer-label]');
    var idSelect = form.querySelector('select[name="id"]');
    var section = form.closest('[data-gw-framed-preview-section]');
    var preview = section ? section.querySelector('[data-gw-framed-preview]') : null;
    var canvas = preview ? preview.querySelector('canvas') : null;
    var caption = preview ? preview.querySelector('.gw-pdp-framed-preview__caption') : null;
    var catalog = null;
    var frameId = DEFAULT_FRAME_ID;
    var linerId = DEFAULT_LINER_ID;
    var drawToken = 0;
    var framePicker = null;
    var enabledCollections = {
      Arber: root.getAttribute('data-enable-arber') !== 'false',
      Tabacchino: root.getAttribute('data-enable-tabacchino') !== 'false',
      Elite: root.getAttribute('data-enable-elite') !== 'false',
    };

    function assetUrl(filename) {
      if (!filename || !catalogUrl) return '';
      var url = catalogUrl.replace(/[^/?]+(\?|$)/, filename + '$1');
      // Theme asset URLs inherit the catalog's immutable cache token. Atlases
      // and thumbs are regenerated in-place, so give them a catalog revision.
      if (filename.indexOf('gw-viz-moulding-') === 0 && catalog && catalog.mouldingVersion) {
        url +=
          (url.indexOf('?') === -1 ? '?' : '&') +
          'moulding_v=' +
          encodeURIComponent(catalog.mouldingVersion);
      }
      if (
        (filename.indexOf('gw-viz-thumb-') === 0 || filename.indexOf('gw-viz-hover-') === 0) &&
        catalog &&
        (catalog.thumbVersion || catalog.mouldingVersion)
      ) {
        url +=
          (url.indexOf('?') === -1 ? '?' : '&') +
          'thumb_v=' +
          encodeURIComponent(catalog.thumbVersion || catalog.mouldingVersion);
      }
      return url;
    }

    function enabledFrames() {
      return (catalog.frames || []).filter(function (frame) {
        if (enabledCollections[frame.collection] === false) return false;
        // Cart path: hide discontinued Roma finishes.
        if (frame.available === false) return false;
        return true;
      });
    }

    function readSelection() {
      if (idSelect && idSelect.selectedIndex >= 0) {
        var opt = idSelect.options[idSelect.selectedIndex];
        var text = opt ? opt.textContent : '';
        return {
          framed: forced || isFramedText(text),
          size: parseSize(text),
        };
      }

      var medium = '';
      var sizeText = '';
      var inputs = form.querySelectorAll(
        '.product-form__input input:checked, .product-form__input select'
      );
      Array.prototype.forEach.call(inputs, function (el) {
        var value = el.tagName === 'SELECT' ? el.value : el.value;
        if (!value) return;
        if (isFramedText(value) || /metal|acrylic/i.test(value)) medium = value;
        if (parseSize(value)) sizeText = value;
      });
      if (!sizeText) {
        var skuEl = document.querySelector('[id^="Sku-"]');
        if (skuEl) sizeText = skuEl.textContent || '';
      }
      return { framed: forced || isFramedText(medium), size: parseSize(sizeText) };
    }

    function setProps(map) {
      PROP_KEYS.forEach(function (key) {
        var input = root.querySelector('[data-prop="' + key + '"]');
        if (!input) return;
        var value = map ? map[key] : '';
        input.value = value || '';
        input.disabled = !value;
      });
    }

    function updatePreview(frame, liner, geom) {
      if (!canvas || !preview || !geom) return;
      var token = (drawToken += 1);
      var printUrl = preview.getAttribute('data-print-url') || '';
      var frameTextureUrl = frame ? assetUrl(frame.mouldingAsset || frame.tileAsset) : '';
      var linerUrl = liner ? assetUrl(liner.sampleAsset) : '';
      var label = 'Your framed presentation';
      if (frame) label += ': ' + frame.name;
      if (liner && !liner.isNone) label += ' · ' + liner.name + ' linen';
      preview.setAttribute('aria-label', label);
      if (caption) caption.textContent = label;

      Promise.all([loadImage(printUrl), loadImage(frameTextureUrl), loadImage(linerUrl)]).then(function (imgs) {
        if (token !== drawToken) return;
        drawPreview(canvas, geom, imgs[0], frame, imgs[1], imgs[2], liner && liner.fill);
      });
    }

    function writeProps() {
      if (!catalog) return;
      var selection = readSelection();
      if (!selection.framed) {
        setProps(null);
        if (outerLabel) outerLabel.textContent = '';
        return;
      }
      var frame = pick(enabledFrames(), frameId, DEFAULT_FRAME_ID);
      var liner = pick(catalog.liners, linerId, DEFAULT_LINER_ID);
      var geom = selection.size
        ? framedOuter(
            selection.size.w,
            selection.size.h,
            frame && frame.faceWidthInches,
            liner && liner.widthInches
          )
        : null;
      var outer = geom
        ? roundIn(geom.outerWidth) + ' × ' + roundIn(geom.outerHeight) + ' in'
        : '';
      setProps({
        'Frame finish': frame ? frame.name : '',
        'Linen liner': liner && !liner.isNone ? liner.name : '',
        'Frame Roma SKU': frame && frame.romaSku ? String(frame.romaSku) : '',
        'Liner Roma SKU': liner && !liner.isNone && liner.romaSku ? String(liner.romaSku) : '',
        'Overall outer': outer,
      });
      if (outerLabel) {
        outerLabel.textContent = outer
          ? 'Overall framed size: ' +
            outer +
            ' (print ' +
            selection.size.w +
            ' × ' +
            selection.size.h +
            ' in)'
          : '';
      }
      updatePreview(frame, liner, geom);
    }

    function swatchButton(item, selectedId, onPick) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gw-pdp-framing__swatch' + (item.id === selectedId ? ' is-active' : '');
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-selected', item.id === selectedId ? 'true' : 'false');
      btn.title = item.name;
      var img = document.createElement('img');
      img.className = 'gw-pdp-framing__swatch-img';
      img.alt = '';
      img.loading = 'lazy';
      img.src = assetUrl(item.sampleAsset);
      btn.appendChild(img);
      var name = document.createElement('span');
      name.className = 'gw-pdp-framing__swatch-name';
      name.textContent = item.name;
      btn.appendChild(name);
      btn.addEventListener('click', onPick);
      return btn;
    }

    function mountFramePicker() {
      if (!catalog || !frameHost) return;
      if (framePicker && framePicker.destroy) framePicker.destroy();
      framePicker = null;
      var api = typeof GWFramePicker !== 'undefined' ? GWFramePicker : null;
      if (!api || !api.mount) return;
      framePicker = api.mount(frameHost, {
        frames: enabledFrames(),
        selectedId: frameId,
        density: 'pdp',
        includeUnavailable: false,
        assetUrl: assetUrl,
        onSelect: function (frame) {
          if (!frame) return;
          frameId = frame.id;
          writeProps();
        },
      });
    }

    function renderLiners() {
      if (!catalog || !linerHost) return;
      linerHost.innerHTML = '';
      (catalog.liners || []).forEach(function (liner) {
        linerHost.appendChild(
          swatchButton(liner, linerId, function () {
            linerId = liner.id;
            renderLiners();
            writeProps();
          })
        );
      });
    }

    function renderSwatches() {
      mountFramePicker();
      renderLiners();
    }

    function sync() {
      var selection = readSelection();
      root.hidden = !selection.framed;
      writeProps();
    }

    fetch(catalogUrl)
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        catalog = json;
        if (catalog.defaults) {
          frameId = catalog.defaults.frameId || frameId;
          linerId = catalog.defaults.linerId || linerId;
        }
        var frames = enabledFrames();
        if (!frames.some(function (frame) { return frame.id === frameId; })) {
          frameId = frames[0] ? frames[0].id : '';
        }
        if (!frames.length) {
          root.hidden = true;
          setProps(null);
          return;
        }
        renderSwatches();
        sync();
      })
      .catch(function () {
        root.hidden = true;
        setProps(null);
      });

    form.addEventListener('change', sync);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, sync);
    }
  }

  function boot() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-gw-pdp-framing]'),
      initPanel
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
